"use client";

import "twin.macro";

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CreateAreaValues, useCreateArea } from "@/api/areas/mutations";
import { Button } from "@/components/buttons";
import Error from "@/components/error";
import Field from "@/components/field";
import Input from "@/components/inputs";
import Label from "@/components/label";

import { GatewaysFieldArray } from "./areaFormFields";

interface CreateAreaModalFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  clientId: string;
}

export const CreateAreaModaleForm = ({
  open,
  setOpen,
  clientId,
}: CreateAreaModalFormProps) => {
  const { t } = useTranslation();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitSuccessful },
  } = useForm<CreateAreaValues>({
    defaultValues: {
      name: "",
      gateways: [{ name: "", latitude: "", longitude: "" }],
    },
  });

  // const { fields, append, prepend, remove, swap, move, insert } = useFieldArray({
  //   control, // control props comes from useForm (optional: if you are using FormProvider)
  //   name: "test", // unique name for your Field Array
  // });

  const closeModalAndResetForm = () => {
    setOpen(false);
    reset();
  };
  const createArea = useCreateArea({ closeModal: closeModalAndResetForm });

  const onSubmit: SubmitHandler<CreateAreaValues> = async (data) => {
    try {
      await createArea.mutateAsync({
        name: data.name,
        gateways: data.gateways.map((gateway) => ({
          name: gateway.name,
          latitude: gateway.latitude || "",
          longitude: gateway.longitude || "",
        })),
        clientId,
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setOpen(false);
    reset({
      name: "",
      gateways: [{ name: "", latitude: "", longitude: "" }],
    });
  }, [isSubmitSuccessful]);

  return (
    <Dialog open={open} onClose={setOpen} tw="relative z-10">
      <DialogBackdrop
        transition
        tw="fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
      />

      <div tw="fixed inset-0 overflow-hidden">
        <div tw="absolute inset-0 overflow-hidden">
          <div tw="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              tw="pointer-events-auto relative w-screen max-w-2xl transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
            >
              <div tw="flex h-full flex-col bg-white py-6 shadow-xl overflow-auto">
                <div tw="px-4 sm:px-6">
                  <div tw="flex items-start justify-between">
                    <DialogTitle tw="mt-6 text-lg font-semibold text-gray-900">
                      {t("form.addArea.title")}
                    </DialogTitle>
                    <div tw="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        tw="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                      >
                        <span tw="absolute -inset-2.5" />
                        <span tw="sr-only">{t("form.close")}</span>
                        <XMarkIcon aria-hidden="true" tw="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div tw="flex flex-col mt-8 gap-6 px-4 sm:px-6">
                    <Field tw="flex-1">
                      <Label>{t("form.addArea.name")}</Label>
                      <Input
                        {...register("name", {
                          required: true,
                        })}
                        invalid={Boolean(errors.name)}
                      />
                      {errors.name?.type === "required" && (
                        <Error>{t("form.mandatory")}</Error>
                      )}
                    </Field>

                    <GatewaysFieldArray<CreateAreaValues>
                      control={control}
                      register={register}
                    />
                  </div>

                  <div tw="flex mt-8 shrink-0 justify-end px-4 py-4 gap-4">
                    <Button white type="button" onClick={() => setOpen(false)}>
                      {t("global.cancel")}
                    </Button>
                    <Button
                      green
                      type="submit"
                      // tw="ml-4 inline-flex justify-center rounded-md bg-primary-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-800"
                    >
                      {t("global.save")}
                    </Button>
                  </div>
                </form>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
